import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFloodgate, useFloodgateDisabled } from '../store/FloodgateStore';
/**
 * useLogFloodgateActivity is a hook that returns a function that
 * can be used by feature to log activity that is relevant to determining
 * if a particular floodgate campaign should be shown.
 *
 * @returns A function that can be used to log activity to the floodgate SDK
 */
export var useLogFloodgateActivity = function () {
    var feedback = useFloodgate();
    var disabled = useFloodgateDisabled();
    var activityQueue = useRef([]);
    var safeGetActivityListener = useMemo(function () {
        return feedback
            ? function () {
                try {
                    var engine = feedback.floodgate.getEngine();
                    return engine.getActivityListener();
                }
                catch (_e) {
                    // The engine can return undefined for any misconfiguration above
                    // including the campaign definitions
                    return undefined;
                }
            }
            : undefined;
    }, [feedback]);
    useEffect(function () {
        // Unload queue when feedback is done loading
        var activityListener = safeGetActivityListener === null || safeGetActivityListener === void 0 ? void 0 : safeGetActivityListener();
        if (activityListener && activityQueue.current.length > 0) {
            activityQueue.current.forEach(function (activity) {
                activityListener.logActivity(activity);
            });
            activityQueue.current = [];
        }
        if (disabled) {
            activityQueue.current = [];
        }
    }, [safeGetActivityListener, disabled]);
    var logActivity = useCallback(function (activity) {
        var _a;
        if (disabled) {
            // Do nothing when we don't have floodgate
            return;
        }
        if (safeGetActivityListener) {
            (_a = safeGetActivityListener()) === null || _a === void 0 ? void 0 : _a.logActivity(activity);
        }
        else {
            activityQueue.current.push(activity);
        }
    }, [safeGetActivityListener, disabled]);
    return logActivity;
};
/**
 * useLogFloodgateCopilotActivity is a hooks that extends
 * useLogFloodgateActivity to also log the copilot usage activity in order
 * to help track general usage of the copilot feature.
 *
 * @returns A function that can be used to log activity to the floodgate SDK
 */
export var useLogFloodgateCopilotActivity = function () {
    var logFloodgateActivity = useLogFloodgateActivity();
    return useCallback(function (activity) {
        logFloodgateActivity(activity);
        logFloodgateActivity('Harmony.CopilotUsageAnyFeatureTransformative');
    }, [logFloodgateActivity]);
};
//# sourceMappingURL=useLogFloodgateActivity.js.map